import Link from 'src/components/primitives/Link/Link';
import {
	BigNum,
	BN,
	PERCENTAGE_PRECISION_EXP,
	PublicKey,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { ReactNode, useEffect, useState } from 'react';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import MarketIcon from 'src/components/Utils/MarketIcon';
import ValueDisplay from 'src/components/ValueDisplay';
import { UiVaults } from 'src/constants/vaults';
import { VAULT_APY_TOOLTIP_CONTENT } from 'src/constants/vaults/misc';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { useVaultAssetSymbols } from '../../../../hooks/vaults/useVaultAssetSymbols';
import { VaultPerformanceStatLabel } from '../VaultPerformanceStatLabel';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import {
	ExternalUiVaultConfig,
	PeriodApy,
	UiVaultConfig,
} from 'src/@types/vaults';
import dayjs from 'dayjs';

const EXTERNAL_VAULT_PLACEHOLDER = '--';

type StatInfo = {
	label: string;
	value: string | ReactNode;
	loading: boolean;
	tooltip?: string;
};

export const VaultPerformanceV2 = (props: {
	depositAssetConfig: SpotMarketConfig;
	vaultPubkey: string;
}) => {
	const spotMarkets = useSpotMarkets();
	const { data: vaultStats, isLoading: isLoadingVaultStats } = useVaultStats(
		props.vaultPubkey
	);
	const uiVaultConfig = UiVaults.getVaultConfig(
		props.vaultPubkey
	) as UiVaultConfig;
	const isExternalVault = !!uiVaultConfig?.externalUiLink;

	const marketSymbol = uiVaultConfig.isNotionalGrowthStrategy
		? 'USDC'
		: props.depositAssetConfig.symbol;

	const vaultClient = useVaultsStore((s) => s.vaultClient);

	const [totalPnl, setTotalPnl] = useState<BigNum | undefined>(undefined);

	useEffect(() => {
		if (vaultStats) {
			if (uiVaultConfig.isNotionalGrowthStrategy) {
				vaultClient
					?.getSubscribedVaultUser(
						new PublicKey(uiVaultConfig.driftUserPubkeyString)
					)
					.then((vaultUser) => {
						const vaultNetQuoteDeposits = vaultUser
							.getUserAccount()
							.totalDeposits.sub(vaultUser.getUserAccount().totalWithdraws);
						const currentNotionalPnl = vaultStats.tvlQuote.sub(
							BigNum.from(vaultNetQuoteDeposits, QUOTE_PRECISION_EXP)
						);

						setTotalPnl(currentNotionalPnl);
					});
			} else {
				setTotalPnl(vaultStats.totalBasePnl);
			}
		}
	}, [vaultStats, uiVaultConfig, vaultClient]);

	const vaultAssetSymbols = useVaultAssetSymbols(props.vaultPubkey);

	const vaultInfoStats: StatInfo[] = [
		{
			label: 'Deposit Asset',
			value: (
				<div className="flex items-center gap-3">
					<MarketIcon
						key={uiVaultConfig.depositAsset}
						marketSymbol={spotMarkets[uiVaultConfig.depositAsset].symbol}
						className="h-4 w-4"
					/>
				</div>
			),
			loading: isLoadingVaultStats,
		},
		{
			label: 'Market',
			value: isExternalVault ? (
				EXTERNAL_VAULT_PLACEHOLDER
			) : (
				<div className="flex items-center gap-3">
					{vaultAssetSymbols.map((symbol) => (
						<MarketIcon
							key={symbol}
							marketSymbol={symbol}
							className="h-4 w-4"
						/>
					))}
				</div>
			),
			loading: isLoadingVaultStats,
		},
		{
			label: 'Performance Fee',
			value: isExternalVault
				? EXTERNAL_VAULT_PLACEHOLDER
				: `${BigNum.from(vaultStats?.profitShare, PERCENTAGE_PRECISION_EXP)
						.mul(new BN(100))
						.toFixed(2)}%`,
			loading: isLoadingVaultStats,
		},
		{
			label: 'Management Fee',
			value: isExternalVault ? (
				EXTERNAL_VAULT_PLACEHOLDER
			) : (
				<span className="mb-lg">
					{(vaultStats?.vaultAccount
						? BigNum.from(
								vaultStats?.vaultAccount.managementFee,
								PERCENTAGE_PRECISION_EXP
							)
						: BigNum.zero()
					)
						.mul(new BN(100))
						.toFixed(2)}
					%
				</span>
			),
			loading: isLoadingVaultStats,
		},
	];

	const apyPeriods: PeriodApy[] = ['90d', '180d', '365d'];
	const apyLabel = isExternalVault
		? 'APY'
		: apyPeriods.map((p) => p.toUpperCase()).join(' / ') + ' APY';
	const apyValue = isExternalVault
		? `${(vaultStats?.apys['90d'] ?? 0).toFixed(2)}%`
		: apyPeriods
				.map((p) => `${(vaultStats?.apys[p] ?? 0).toFixed(2)}%`)
				.join(' / ');

	const vaultPerformanceStats: StatInfo[] = [
		{
			label: apyLabel,
			value: apyValue,
			loading: isLoadingVaultStats,
			tooltip: VAULT_APY_TOOLTIP_CONTENT,
		},
		{
			label: '30D Volume',
			value: isExternalVault
				? EXTERNAL_VAULT_PLACEHOLDER
				: `$${vaultStats?.volume30Days.toMillified()}`,
			loading: isLoadingVaultStats,
		},
		{
			label: 'Vault Age',
			value: (() => {
				if (vaultStats?.numOfVaultSnapshots) {
					return `${vaultStats.numOfVaultSnapshots} days`;
				}
				if (isExternalVault) {
					const startTs = (uiVaultConfig as ExternalUiVaultConfig).startTs;
					return `${dayjs().diff(dayjs.unix(startTs), 'days')} days`;
				}
				return '-';
			})(),
			loading: isLoadingVaultStats,
		},
		{
			label: 'TVL',
			value: `${vaultStats?.tvlBase.toMillified()}`,
			loading: isLoadingVaultStats,
		},
		{
			label: 'Capacity',
			value: vaultStats?.isUncappedCapacity
				? 'Uncapped'
				: `${vaultStats?.capacityPct.toFixed(2)}%`,
			loading: isLoadingVaultStats,
		},
	];

	const returnStats: StatInfo[] = [
		{
			label: 'Total Earnings',
			value: isExternalVault ? (
				EXTERNAL_VAULT_PLACEHOLDER
			) : (
				<span className="flex items-center gap-1">
					<MarketIcon marketSymbol={marketSymbol} className="h-4 w-4" />
					{totalPnl?.prettyPrint(true)}
				</span>
			),
			loading: isLoadingVaultStats,
			tooltip:
				'The total earnings for the entire vault. Total Earnings = Total Vault TVL - Total Net Deposits.',
		},
		{
			label: 'Max Daily Drawdown',
			value: isExternalVault
				? EXTERNAL_VAULT_PLACEHOLDER
				: `${(vaultStats?.maxDrawdownPct ?? 0).toFixed(2)}%`,
			loading: isLoadingVaultStats,
			tooltip: "The maximum day-to-day loss in the vault's history.",
		},
	];

	return (
		<div className="flex w-full flex-col gap-y-lg">
			<span className="typo-t3 text-text-default">About</span>
			<div className="flex flex-col gap-y-sm">
				{vaultInfoStats.map((stat) => {
					return (
						<ValueDisplay.Default
							key={stat.label}
							label={
								<VaultPerformanceStatLabel
									label={stat.label}
									tooltip={stat.tooltip}
								/>
							}
							value={
								stat.loading ? (
									<SkeletonValuePlaceholder className="h-6 w-20" loading />
								) : (
									stat.value
								)
							}
						/>
					);
				})}
			</div>
			<div className="flex flex-col gap-y-sm">
				{vaultPerformanceStats.map((stat) => {
					return (
						<ValueDisplay.Default
							key={stat.label}
							label={
								<VaultPerformanceStatLabel
									label={stat.label}
									tooltip={stat.tooltip}
								/>
							}
							value={
								stat.loading ? (
									<SkeletonValuePlaceholder className="h-6 w-20" loading />
								) : (
									stat.value
								)
							}
						/>
					);
				})}
			</div>
			<div className="flex flex-col gap-y-sm">
				{returnStats.map((stat) => {
					return (
						<ValueDisplay.Default
							key={stat.label}
							label={
								<VaultPerformanceStatLabel
									label={stat.label}
									tooltip={stat.tooltip}
								/>
							}
							value={
								stat.loading ? (
									<SkeletonValuePlaceholder className="h-6 w-20" loading />
								) : (
									stat.value
								)
							}
						/>
					);
				})}
			</div>
			<ValueDisplay.Default
				key="Social Media"
				label="Social Media"
				value={
					isLoadingVaultStats ? (
						<SkeletonValuePlaceholder className="h-6 w-20" loading />
					) : (
						<Link
							variant="secondary"
							href={`https://x.com/${uiVaultConfig.vaultManager.twitterHandle}`}
							openInNewTab
						>
							@{uiVaultConfig.vaultManager.twitterHandle}
						</Link>
					)
				}
			/>
		</div>
	);
};
